import { useMutation } from '@tanstack/react-query';
import { examinerEvidenceClient } from '../api/client';
import type { components } from '@aaimplatform/institutional-types/examiner-evidence-package';

/** Re-exported generated types so page components import from one place. */
export type EvidencePackageRequest = components['schemas']['EvidencePackageRequest'];
export type EvidencePackageManifest = components['schemas']['EvidencePackageManifest'];
export type EvidenceArtifactEntry = components['schemas']['EvidenceArtifactEntry'];
export type EvidenceExternalReference = components['schemas']['EvidenceExternalReference'];

/**
 * Error carrying the spec'd ErrorResponse discriminants so the page can
 * distinguish the fail-closed 502 CORE_EVIDENCE_UNAVAILABLE case (UCC
 * upstream configured but unreachable -- no package/manifest was produced)
 * from generic request failures.
 */
export class EvidencePackageError extends Error {
  constructor(
    message: string,
    public readonly code?: string,
    public readonly status?: number
  ) {
    super(message);
    this.name = 'EvidencePackageError';
  }

  get isCoreEvidenceUnavailable(): boolean {
    // Classify on the CODE alone. The backend ALWAYS sets code
    // CORE_EVIDENCE_UNAVAILABLE on its own fail-closed 502s (UCC upstream
    // configured but unreachable). Keying additionally on status === 502 would
    // misclassify an infrastructure 502 (e.g. platform-admin-api itself down)
    // as the UCC fail-closed case and render the UCC-specific banner. GH #1930.
    return this.code === 'CORE_EVIDENCE_UNAVAILABLE';
  }
}

/**
 * Narrow an openapi-fetch error payload (spec ErrorResponse shape) into an
 * EvidencePackageError. Non-2xx bodies are `{ error, message?, code? }` per
 * schema-registry/examiner-evidence-package.openapi.yaml.
 */
function toEvidencePackageError(
  error: unknown,
  status: number | undefined,
  fallbackMessage: string
): EvidencePackageError {
  const body = (error ?? {}) as { error?: string; message?: string; code?: string };
  return new EvidencePackageError(
    body.message ?? body.error ?? fallbackMessage,
    body.code,
    status
  );
}

/**
 * Preview the evidence package integrity manifest (no ZIP bytes).
 *
 * POST /api/admin/examiners/evidence-package/manifest on platform-admin-api.
 * Returns the EvidencePackageManifest (artifacts + external references +
 * integrity block + the explicit coreBundled flag). Throws
 * EvidencePackageError; the 502 fail-closed case sets
 * isCoreEvidenceUnavailable.
 */
export function usePreviewManifest() {
  return useMutation({
    mutationFn: async (request: EvidencePackageRequest): Promise<EvidencePackageManifest> => {
      const { data, error, response } = await examinerEvidenceClient.POST(
        '/api/admin/examiners/evidence-package/manifest',
        { body: request }
      );
      // openapi-fetch yields `error: undefined` for a non-ok response with an
      // empty body (Content-Length 0), so also gate on response.ok.
      if (error || !response.ok) {
        throw toEvidencePackageError(error, response?.status, 'Manifest preview failed');
      }
      return data;
    },
  });
}

/** Default download filename when the server sends no Content-Disposition. */
const FALLBACK_ZIP_FILENAME = 'examiner-evidence-package.zip';

/**
 * Extract the filename from a Content-Disposition header value, when present.
 */
function filenameFromContentDisposition(headerValue: string | null): string | null {
  if (!headerValue) return null;
  const match = headerValue.match(/filename\*?=(?:UTF-8''|")?([^";]+)"?/i);
  return match ? decodeURIComponent(match[1].trim()) : null;
}

/**
 * Assemble and download the examiner evidence package ZIP.
 *
 * POST /api/admin/examiners/evidence-package with parseAs: 'blob' (the
 * useRegulatoryReadiness useDownloadReport precedent): the spec types the
 * 200 body as `"application/zip": string`; at runtime parseAs overrides
 * parsing and yields a Blob, so the cast to `unknown as Blob` is correct.
 * The blob is delivered via a transient object-URL anchor click.
 *
 * Errors throw EvidencePackageError. On the fail-closed 502
 * CORE_EVIDENCE_UNAVAILABLE path no ZIP is produced server-side, so there
 * is nothing partial to save -- the page surfaces the distinct banner.
 */
export function useDownloadEvidencePackage() {
  return useMutation({
    mutationFn: async (request: EvidencePackageRequest): Promise<void> => {
      const { data, error, response } = await examinerEvidenceClient.POST(
        '/api/admin/examiners/evidence-package',
        { body: request, parseAs: 'blob' }
      );
      // Non-ok bodies are parsed as JSON by openapi-fetch even under
      // parseAs: 'blob'; an empty non-ok body yields error: undefined, so
      // also gate on response.ok.
      if (error || !response.ok) {
        throw toEvidencePackageError(error, response?.status, 'Evidence package download failed');
      }

      // parseAs: 'blob' yields a Blob at runtime; the spec types it as string.
      const blob = data as unknown as Blob;
      const filename =
        filenameFromContentDisposition(response?.headers?.get('content-disposition') ?? null) ??
        FALLBACK_ZIP_FILENAME;

      const downloadUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(downloadUrl);
    },
  });
}
